import { ExportDetails } from "../../../../types/niffler";
import {
  EXPORT_INCLUSION_TYPES,
  EXPORT_DATA_TYPES,
  EXPORT_RELATED_FILTERS,
} from "../types";
import { EXPORT_STATUS, EXPORT_TYPE } from "../constants";
import { isReadOnlyUser } from "../../../../utils/auth";

/**
 * Formats a date string to a readable format
 */
export function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateString; // fallback to original if parsing fails
  }
}

/**
 * Maps API status values to user-friendly display text
 * From PRD: "InProgress" → "Processing", "Completed" → "Completed", others → "Failed"
 */
export function mapStatus(apiStatus: string): string {
  switch (apiStatus) {
    case EXPORT_STATUS.COMPLETED:
      return "Completed";
    case EXPORT_STATUS.IN_PROGRESS:
      return "Processing";
    default:
      return "Failed";
  }
}

/**
 * Maps API status to appropriate color for status badges
 */
export function getStatusColor(
  apiStatus: string
): "success" | "info" | "error" {
  switch (apiStatus) {
    case EXPORT_STATUS.COMPLETED:
      return "success";
    case EXPORT_STATUS.IN_PROGRESS:
      return "info";
    default:
      return "error";
  }
}

/**
 * Maps export type to user-friendly display name with format
 * From PRD: companyList → "Company List (.XLSX)", companyProfile → "Company Profile (.XLSX)"
 * For companyProfile, shows custom title + "Company Profile" + format
 */
export function mapExportType(exportDetails: ExportDetails): string {
  const format = `.${exportDetails.format}`;

  switch (exportDetails.exportType) {
    case EXPORT_TYPE.COMPANY_LIST:
      return `Company List (${format})`;
    case EXPORT_TYPE.COMPANY_PROFILE:
      return `${exportDetails.params?.title || ""} Company Profile (${format})`;
    default:
      return `${exportDetails.exportType} (${format})`; // fallback to original value with format
  }
}

/**
 * Gets export type display name WITHOUT format
 * For use in table where format has its own column
 */
export function getExportTypeDisplay(exportDetails: ExportDetails): string {
  switch (exportDetails.exportType) {
    case EXPORT_TYPE.COMPANY_LIST:
      return "Company List";
    case EXPORT_TYPE.COMPANY_PROFILE:
      return `${exportDetails.params?.title || ""} Company Profile`;
    default:
      return exportDetails.exportType; // fallback to original value
  }
}

/**
 * Gets inclusion value text based on inclusion type and filter type
 * Based on legacy DataInclusions component logic
 */
export function getInclusionValue(
  inclusion: EXPORT_INCLUSION_TYPES,
  filterType?: EXPORT_RELATED_FILTERS
): string {
  switch (inclusion) {
    case EXPORT_INCLUSION_TYPES.ALL:
      return "All data";
    case EXPORT_INCLUSION_TYPES.FILTERS:
      return `Data which matches the ${filterType} filters selected`;
    case EXPORT_INCLUSION_TYPES.SELECTED:
      return "Specific subset selected";
    default:
      return "";
  }
}

/**
 * Gets included data items for display in modal
 * Returns array of objects with data type and inclusion description
 */
export function getIncludedDataItems(
  exportDetails: ExportDetails
): Array<{ dataType: string; inclusion: string }> {
  const { params } = exportDetails;
  const items: Array<{ dataType: string; inclusion: string }> = [];

  // Product Installs
  if (
    params?.installInclusion &&
    params.installInclusion !== EXPORT_INCLUSION_TYPES.NONE
  ) {
    items.push({
      dataType: EXPORT_DATA_TYPES.INSTALL,
      inclusion: getInclusionValue(
        params.installInclusion,
        EXPORT_RELATED_FILTERS.PRODUCTS
      ),
    });
  }

  // Contracts
  if (
    params?.contractsInclusion &&
    params.contractsInclusion !== EXPORT_INCLUSION_TYPES.NONE
  ) {
    items.push({
      dataType: EXPORT_DATA_TYPES.CONTRACTS,
      inclusion: getInclusionValue(
        params.contractsInclusion,
        EXPORT_RELATED_FILTERS.CONTRACTS
      ),
    });
  }

  // Intent Activity
  if (
    params?.intentInclusion &&
    params.intentInclusion !== EXPORT_INCLUSION_TYPES.NONE
  ) {
    items.push({
      dataType: EXPORT_DATA_TYPES.INTENT,
      inclusion: getInclusionValue(
        params.intentInclusion,
        EXPORT_RELATED_FILTERS.INTENT
      ),
    });
  }

  // Functional Area Intelligence
  if (
    params?.faiInclusion &&
    params.faiInclusion !== EXPORT_INCLUSION_TYPES.NONE
  ) {
    items.push({
      dataType: EXPORT_DATA_TYPES.FAI,
      inclusion: getInclusionValue(params.faiInclusion),
    });
  }

  return items;
}

/**
 * Checks if an export can be downloaded
 * Available for exports with "Completed" status and valid s3url
 */
export function canDownloadExport(exportDetails: ExportDetails): boolean {
  return (
    exportDetails.status === EXPORT_STATUS.COMPLETED && !!exportDetails.s3url
  );
}

/**
 * Checks if an export can be resubmitted
 * Available for exports with "Failed" status or missing s3url, never for
 * read-only (RBAC v2) users: resubmitting re-runs a credit-consuming export
 */
export function canResubmitExport(exportDetails: ExportDetails): boolean {
  if (isReadOnlyUser()) return false;
  return (
    exportDetails.status !== EXPORT_STATUS.COMPLETED || !exportDetails.s3url
  );
}

/**
 * Formats search criteria as a bullet list
 */
export function formatSearchCriteria(criteria?: string[]): string[] {
  if (!criteria || criteria.length === 0) {
    return ["No specific criteria"];
  }
  return criteria;
}
